// shifts-v2: Month view + self-service
// agent ใส่ของตัวเองได้, tenant แก้ได้ทุกคน, ทุกคนเห็นทั้งทีม
import React, { useState, useEffect, useMemo } from 'react';
import API from '../api';
import toast from 'react-hot-toast';

const DAY_LABELS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const MONTH_NAMES = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                     'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

// ============= helpers =============
function parseUTC(ts) {
  if (!ts) return null;
  if (typeof ts !== 'string') return new Date(ts);
  if (/[Z+-]\d{0,2}:?\d{0,2}$/.test(ts)) return new Date(ts);
  return new Date(ts + 'Z');
}

function bkkDateStr(d) {
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const o = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${o.year}-${o.month}-${o.day}`;
}

function formatHM(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('th-TH', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function bkkDatetimeLocal(d) {
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d);
  const o = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${o.year}-${o.month}-${o.day}T${o.hour}:${o.minute}`;
}

// สีคงที่ของ agent (deterministic จาก agent_id)
function colorFromId(id) {
  const palette = [
    '#0ea5e9', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6',
    '#14b8a6', '#f43f5e', '#3b82f6', '#84cc16', '#a855f7',
    '#06b6d4', '#eab308',
  ];
  if (!id) return palette[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

// ============= main component =============
export default function Shifts() {
  const today = new Date();
  const [year, setYear]   = useState(parseInt(bkkDateStr(today).slice(0, 4), 10));
  const [month, setMonth] = useState(parseInt(bkkDateStr(today).slice(5, 7), 10) - 1); // 0-11

  const [shifts, setShifts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // null | { type:'new', date }, shift object
  const [filterAgent, setFilterAgent] = useState('all');

  // user role from JWT
  const { userRole, userId } = useMemo(() => {
    try {
      const tk = localStorage.getItem('token');
      if (!tk) return { userRole: null, userId: null };
      const p = JSON.parse(atob(tk.split('.')[1]));
      return { userRole: p.role || null, userId: p.id || null };
    } catch { return { userRole: null, userId: null }; }
  }, []);

  const isTenant = userRole === 'tenant';
  const isAgent  = userRole === 'agent';

  // วันแรกและสุดท้ายของเดือน + padding ให้ครบสัปดาห์อาทิตย์-เสาร์
  const calendar = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const startDay = firstOfMonth.getDay(); // 0=Sun ... 6=Sat
    const lastDate = new Date(year, month + 1, 0).getDate();
    const cells = [];
    // padding ก่อนวันที่ 1
    for (let i = 0; i < startDay; i++) {
      const d = new Date(year, month, 1 - (startDay - i));
      cells.push({ date: d, inMonth: false });
    }
    // วันในเดือน
    for (let day = 1; day <= lastDate; day++) {
      cells.push({ date: new Date(year, month, day), inMonth: true });
    }
    // padding หลังวันสุดท้าย ให้ครบ 6 สัปดาห์
    while (cells.length < 42) {
      const last = cells[cells.length - 1].date;
      const d = new Date(last); d.setDate(last.getDate() + 1);
      cells.push({ date: d, inMonth: false });
    }
    return cells;
  }, [year, month]);

  const [from, to] = useMemo(() => [
    bkkDateStr(calendar[0].date),
    bkkDateStr(calendar[calendar.length - 1].date),
  ], [calendar]);

  async function load() {
    setLoading(true);
    try {
      const [sh, ag] = await Promise.all([
        API.get(`/api/shifts?from=${from}&to=${to}`),
        API.get('/api/shifts/agents/list'),
      ]);
      if (sh.data?.success) setShifts(sh.data.data || []);
      if (ag.data?.success) setAgents(ag.data.data || []);
    } catch (e) {
      console.error(e);
      toast.error('โหลดข้อมูลล้มเหลว');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  // group + filter shifts by date
  const byDate = useMemo(() => {
    const map = {};
    shifts.forEach((s) => {
      if (filterAgent !== 'all' && s.agent_id !== filterAgent) return;
      const key = bkkDateStr(parseUTC(s.start_at));
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    // sort by start time
    Object.values(map).forEach(arr => arr.sort((a, b) => a.start_at.localeCompare(b.start_at)));
    return map;
  }, [shifts, filterAgent]);

  function gotoMonth(delta) {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y);
  }

  function gotoToday() {
    setYear(parseInt(bkkDateStr(new Date()).slice(0, 4), 10));
    setMonth(parseInt(bkkDateStr(new Date()).slice(5, 7), 10) - 1);
  }

  async function handleDelete(id) {
    if (!window.confirm('ลบกะนี้?')) return;
    try {
      await API.delete(`/api/shifts/${id}`);
      toast.success('ลบแล้ว');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'ลบไม่สำเร็จ');
    }
  }

  // user คลิกที่วันใดๆ
  function handleDayClick(d, inMonth) {
    if (!inMonth) return;
    if (!isTenant && !isAgent) return;
    setEditing({ type: 'new', date: d });
  }

  // user คลิกที่ shift เฉพาะตัว
  function handleShiftClick(e, shift) {
    e.stopPropagation();
    // agent: คลิกของคนอื่นไม่เปิด modal
    if (isAgent && shift.agent_id !== userId) return;
    setEditing(shift);
  }

  const todayKey = bkkDateStr(new Date());

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>📅 ตารางเวร</h2>
        <button onClick={() => gotoMonth(-1)} style={btnNav}>← เดือนก่อน</button>
        <button onClick={gotoToday} style={btnNav}>วันนี้</button>
        <button onClick={() => gotoMonth(1)} style={btnNav}>เดือนหน้า →</button>
        <span style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>
          {MONTH_NAMES[month]} {year + 543}
        </span>
        {/* Filter by agent */}
        <select value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
          <option value="all">👥 ทุกคน</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        {(isTenant || isAgent) && (
          <button onClick={() => setEditing({ type: 'new', date: new Date() })} style={{
            marginLeft: 'auto', background: '#0ea5e9', color: '#fff',
            border: 'none', padding: '8px 16px', borderRadius: 8,
            cursor: 'pointer', fontWeight: 600,
          }}>
            ➕ เพิ่มกะ
          </button>
        )}
      </div>

      {loading && <div style={{ color: '#64748b', marginBottom: 8 }}>กำลังโหลด...</div>}

      {/* Day labels */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
        marginBottom: 4,
      }}>
        {DAY_LABELS.map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 12, fontWeight: 700,
            color: i === 0 ? '#dc2626' : i === 6 ? '#0ea5e9' : '#64748b',
            padding: '6px 0',
          }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
        background: '#fff', padding: 4, borderRadius: 10,
        border: '1px solid #e2e8f0',
      }}>
        {calendar.map((cell, idx) => {
          const key = bkkDateStr(cell.date);
          const list = byDate[key] || [];
          const isToday = key === todayKey;
          const dayOfWeek = idx % 7;
          return (
            <div key={key + idx}
              onClick={() => handleDayClick(cell.date, cell.inMonth)}
              style={{
                minHeight: 100, padding: 6, borderRadius: 6,
                background: isToday ? '#f0f9ff'
                  : cell.inMonth ? '#fff' : '#f8fafc',
                border: isToday ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                cursor: cell.inMonth && (isTenant || isAgent) ? 'pointer' : 'default',
                opacity: cell.inMonth ? 1 : 0.4,
                position: 'relative',
              }}>
              <div style={{
                fontSize: 11, fontWeight: 700, marginBottom: 4,
                color: isToday ? '#0ea5e9'
                  : dayOfWeek === 0 ? '#dc2626'
                  : dayOfWeek === 6 ? '#0ea5e9'
                  : '#475569',
              }}>
                {cell.date.getDate()}
              </div>
              {list.slice(0, 4).map((s) => {
                const isMine = s.agent_id === userId;
                const canEdit = isTenant || isMine;
                return (
                  <div key={s.id}
                    onClick={(e) => handleShiftClick(e, s)}
                    title={`${s.agent_name} ${formatHM(parseUTC(s.start_at))}-${formatHM(parseUTC(s.end_at))}${s.notes ? ' · ' + s.notes : ''}`}
                    style={{
                      background: colorFromId(s.agent_id),
                      color: '#fff',
                      padding: '2px 5px', borderRadius: 4, marginBottom: 2,
                      fontSize: 10, lineHeight: 1.3, cursor: canEdit ? 'pointer' : 'default',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      border: isMine ? '1.5px solid #0f172a' : 'none',
                    }}>
                    {formatHM(parseUTC(s.start_at))} {s.agent_name}
                  </div>
                );
              })}
              {list.length > 4 && (
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                  +{list.length - 4} อื่นๆ
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend: agents with color */}
      <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
        <span style={{ color: '#64748b' }}>👥 ทีม:</span>
        {agents.map((a) => (
          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              width: 10, height: 10, borderRadius: 2,
              background: colorFromId(a.id), display: 'inline-block',
            }} />
            <span style={a.id === userId ? { fontWeight: 700, color: '#0f172a' } : {}}>
              {a.name}{a.id === userId ? ' (ฉัน)' : ''}
            </span>
          </span>
        ))}
      </div>

      {/* Modal */}
      {editing && (
        <ShiftModal
          shift={editing.type === 'new' ? null : editing}
          initialDate={editing.type === 'new' ? editing.date : null}
          agents={agents}
          isTenant={isTenant}
          userId={userId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={editing.type === 'new' ? null : () => { handleDelete(editing.id); setEditing(null); }}
        />
      )}
    </div>
  );
}

const btnNav = {
  background: '#fff', border: '1px solid #cbd5e1', padding: '6px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#475569',
};

// ============= Modal =============
function ShiftModal({ shift, initialDate, agents, isTenant, userId, onClose, onSaved, onDelete }) {
  const isNew = !shift;
  const myAgent = agents.find((a) => a.id === userId);

  // default agent_id: tenant ดูได้ทุกคน → first agent. agent → ตัวเอง
  const [agentId, setAgentId] = useState(
    shift?.agent_id
    || (isTenant ? (agents[0]?.id || '') : userId)
  );

  // เริ่มต้นวันที่ที่กดมา 09:00
  const defaultDate = useMemo(() => {
    const d = initialDate ? new Date(initialDate) : new Date();
    d.setHours(9, 0, 0, 0);
    return d;
  }, [initialDate]);
  const defaultEnd = useMemo(() => {
    const d = new Date(defaultDate); d.setHours(17, 0, 0, 0);
    return d;
  }, [defaultDate]);

  const [startLocal, setStartLocal] = useState(
    shift ? bkkDatetimeLocal(parseUTC(shift.start_at)) : bkkDatetimeLocal(defaultDate)
  );
  const [endLocal, setEndLocal] = useState(
    shift ? bkkDatetimeLocal(parseUTC(shift.end_at)) : bkkDatetimeLocal(defaultEnd)
  );
  const [role, setRole]   = useState(shift?.role || 'agent');
  const [notes, setNotes] = useState(shift?.notes || '');
  const [status, setStatus] = useState(shift?.status || 'scheduled');
  const [saving, setSaving] = useState(false);

  function toBkkISO(local) { const d = new Date(local + ":00+07:00"); return d.toISOString().slice(0, 19); }

  async function handleSave() {
    if (!agentId) return toast.error('เลือก agent');
    if (!startLocal || !endLocal) return toast.error('ระบุเวลา');
    if (endLocal <= startLocal) return toast.error('เวลาสิ้นสุดต้องหลังเริ่ม');
    setSaving(true);
    try {
      const payload = {
        start_at: toBkkISO(startLocal),
        end_at:   toBkkISO(endLocal),
        role, notes: notes || null,
      };
      // tenant ส่ง agent_id ได้, agent ไม่ต้อง (backend จะใช้ตัวเอง)
      if (isTenant) payload.agent_id = agentId;

      if (isNew) {
        await API.post('/api/shifts', payload);
        toast.success('เพิ่มกะแล้ว');
      } else {
        await API.put(`/api/shifts/${shift.id}`, { ...payload, status });
        toast.success('บันทึกแล้ว');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'ผิดพลาด');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 24,
        width: 420, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: 0, marginBottom: 16 }}>
          {isNew ? '➕ เพิ่มกะ' : '✏️ แก้กะ'}
        </h3>

        {/* Agent selector: tenant เลือกได้, agent ดูเฉยๆ */}
        <Field label="Agent">
          {isTenant ? (
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)} style={inp}>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.email})
                </option>
              ))}
            </select>
          ) : (
            <input type="text" value={myAgent?.name || 'ฉัน'} disabled style={{ ...inp, background: '#f1f5f9' }} />
          )}
        </Field>

        <Field label="เวลาเริ่ม">
          <input type="datetime-local" value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)} style={inp} />
        </Field>

        <Field label="เวลาสิ้นสุด">
          <input type="datetime-local" value={endLocal}
            onChange={(e) => setEndLocal(e.target.value)} style={inp} />
        </Field>

        <Field label="ตำแหน่ง">
          <select value={role} onChange={(e) => setRole(e.target.value)} style={inp}>
            <option value="agent">agent</option>
            <option value="supervisor">supervisor</option>
            <option value="on-call">on-call</option>
          </select>
        </Field>

        {!isNew && (
          <Field label="สถานะ">
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={inp}>
              <option value="scheduled">scheduled</option>
              <option value="active">active</option>
              <option value="completed">completed</option>
              <option value="missed">missed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </Field>
        )}

        <Field label="หมายเหตุ">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            style={{ ...inp, height: 60, resize: 'vertical' }} />
        </Field>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={saving} style={{
            background: '#0ea5e9', color: '#fff', border: 'none',
            padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, flex: 1,
          }}>{saving ? 'กำลังบันทึก...' : '💾 บันทึก'}</button>
          <button onClick={onClose} style={{
            background: '#e5e7eb', border: 'none',
            padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
          }}>ยกเลิก</button>
          {onDelete && (
            <button onClick={onDelete} style={{
              background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
              padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
            }}>🗑 ลบ</button>
          )}
        </div>
      </div>
    </div>
  );
}

const inp = {
  width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
  borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
